import { ChangeDetectionStrategy, Component, ElementRef, Injector, OnInit, inject } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {
  IonButton,
  IonButtons,
  IonCol,
  IonContent,
  IonFooter,
  IonGrid,
  IonHeader,
  IonIcon,
  IonInput,
  IonRow,
  IonTitle,
  IonToolbar,
  LoadingController,
  ModalController,
} from '@ionic/angular/standalone';
import { markFormAsInvalid } from '@okcargo-apps/layout-ionic';
import { MessagesUiService } from '@okcargo-apps/messages-ui';
import {
  DecaStopType,
  IDecaDocument,
  IDecaDocumentSegment,
  isDecaServicePriorToTimestamp,
  UUID,
} from '@okcargo-apps/shared-domain';
import { format, isAfter, isBefore, parseISO } from 'date-fns';
import { DecaEditReasonComponent } from '../deca-edit-reason/deca-edit-reason.component';
import { DecaEditDateForm, DecaEditReasonFormModel } from '../deca-forms.model';
import { DecaListItemComponent } from '../deca-list-item/deca-list-item.component';
import { DecaMutationsService } from '../deca-mutations.service';
import { DecaStoreService } from '../deca-store.service';

@Component({
  selector: 'lib-deca-edit-date-modal',
  templateUrl: './deca-edit-date-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    IonCol,
    IonRow,
    IonGrid,
    IonFooter,
    ReactiveFormsModule,
    IonContent,
    IonInput,
    IonTitle,
    IonIcon,
    IonButton,
    IonToolbar,
    IonButtons,
    IonHeader,
    DecaEditReasonComponent,
    DecaListItemComponent,
  ],
})
export class DecaEditDateModalComponent implements OnInit {
  // comes from the component that opens the modal
  public readonly document!: IDecaDocument;
  public readonly userCompanyId!: UUID;
  public readonly stopType!: DecaStopType;
  public readonly segment!: IDecaDocumentSegment;

  private readonly fb = inject(FormBuilder);
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly injector = inject(Injector);
  private readonly modalCtrl = inject(ModalController);
  private readonly loadingCtrl = inject(LoadingController);
  private readonly decaMutationsService = inject(DecaMutationsService);
  private readonly decaStoreService = inject(DecaStoreService);
  private readonly messagesUiService = inject(MessagesUiService);

  protected readonly form: FormGroup<DecaEditDateForm> = this.fb.group<DecaEditDateForm>({
    date: this.fb.nonNullable.control(''),
    changeReason: this.fb.group<DecaEditReasonFormModel>({ reason: this.fb.nonNullable.control('') }),
  });

  protected get isServiceStarted(): boolean {
    return isDecaServicePriorToTimestamp(this.document, new Date());
  }

  private requireChangeReasonWhenServiceStarted(): void {
    if (!this.isServiceStarted) return;

    const reason = this.form.controls.changeReason.controls.reason;
    reason.addValidators(Validators.required);
    reason.updateValueAndValidity({ emitEvent: false });
  }

  ngOnInit(): void {
    this.requireChangeReasonWhenServiceStarted();
    const stop = this.segment[this.stopType];
    const initialDateStr = stop?.date ? format(new Date(stop.date), "yyyy-MM-dd'T'HH:mm") : '';

    this.form.controls.date.setValue(initialDateStr);
    this.form.controls.date.setValidators([this.dateTimeValidator()]);
    this.form.controls.date.updateValueAndValidity();
  }

  protected get modalTitle(): string {
    return this.stopType === 'origin' ? 'Editar fecha de origen' : 'Editar fecha de destino';
  }

  protected get inputLabel(): string {
    return this.stopType === 'origin' ? 'Fecha y hora de origen' : 'Fecha y hora de destino';
  }

  protected get errorText(): string {
    const control = this.form.controls.date;
    if (control.hasError('required')) return 'Debe seleccionar una fecha y hora para poder realizar el cambio';
    if (control.hasError('beforeNow')) return 'La fecha no puede ser anterior al momento actual';
    if (control.hasError('afterDestination')) return 'La fecha de origen debe ser anterior a la fecha de destino';
    if (control.hasError('beforeOrigin')) return 'La fecha de destino debe ser posterior a la fecha de origen';
    return '';
  }

  private dateTimeValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value) return { required: true };

      const selectedDate = parseISO(value);
      const now = new Date();

      if (isBefore(selectedDate, now)) {
        return { beforeNow: true };
      }

      if (this.stopType === 'origin' && this.segment.destination.date) {
        const destDate = new Date(this.segment.destination.date);
        if (!isBefore(selectedDate, destDate)) {
          return { afterDestination: true };
        }
      }

      if (this.stopType === 'destination' && this.segment.origin.date) {
        const origDate = new Date(this.segment.origin.date);
        if (!isAfter(selectedDate, origDate)) {
          return { beforeOrigin: true };
        }
      }

      return null;
    };
  }

  protected async saveChanges(): Promise<void> {
    if (this.form.invalid) {
      markFormAsInvalid(this.form, this.elementRef.nativeElement, this.injector);
      return;
    }

    const loading = await this.loadingCtrl.create({
      message: 'Enviando datos...',
    });
    await loading.present();

    try {
      const selectedDate = parseISO(this.form.controls.date.value);

      const updateSegment: IDecaDocumentSegment = {
        ...this.segment,
        [this.stopType]: {
          ...this.segment[this.stopType],
          date: selectedDate,
        },
      };

      const updatedDocument: IDecaDocument = {
        ...this.document,
        segments: this.document.segments.map((segment) => (segment.id === updateSegment.id ? updateSegment : segment)),
      };

      await this.decaMutationsService.updateStopDate(
        updatedDocument,
        this.stopType,
        this.segment.id,
        this.form.controls.changeReason.controls.reason.value.trim(),
      );

      const stopTypeLabel = this.stopType === 'origin' ? 'origen' : 'destino';
      this.messagesUiService.setMessage({
        objectId: updatedDocument.id,
        message: [`Ha enviado una nueva fecha de ${stopTypeLabel} a este documento`],
        durationMs: 3000,
      });

      await new Promise((resolve) => setTimeout(resolve, 500));

      this.decaStoreService.reloadAfterSaveData();

      this.modalCtrl.dismiss(updatedDocument, 'confirm');
    } catch (error) {
      // TODO manage error
      console.error('Error updating date', error);
    } finally {
      await loading.dismiss();
    }
  }

  protected closeModalWithCancel(): Promise<boolean> {
    return this.modalCtrl.dismiss(null, 'cancel');
  }
}
